import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { ExtensionPrivileges } from 'src/app/pages/extension-details/models/extension-privileges';
import { timeBasedRouteItemDialogSubmit } from 'src/app/pages/extension-details/state/extension-detail.actions';
import { AddTimeBasedRouteComponent } from '../add-time-based-route/containers/add-time-based-route.component';
import { ExtensionDetailView } from '../extension-details/models/extension-detail-view.model';
import { TimeBasedRouteItemView } from './models/extension-time-based-routes.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-time-based-routes',
    styleUrls: ['./extension-time-based-routes.component.scss'],
    templateUrl: './extension-time-based-routes.component.html',
    standalone: false
})
export class ExtensionTimeBasedRoutesComponent implements ControlValueAccessor, OnDestroy {
   @Input() callState: CallState;

   @Input() extensionPrivileges: ExtensionPrivileges;

   @Input() canEdit: boolean;

   @Input() extensionType: string;

   @Input() extension: ExtensionDetailView | null;

   @Output() addTimeBasedRouteClick = new EventEmitter<void>();

   @Output() editTimeBasedRouteClick = new EventEmitter<number>();

   extensionTimeBasedRouteItems: TimeBasedRouteItemView[] = [];

   disabled = false;

   onTouched: () => void;

   private destroyed$ = new Subject<void>();

   private onChange: (value: Array<TimeBasedRouteItemView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      private dialogService: LcsDialogService
   ) {
      control.valueAccessor = this;
      this.actions$.pipe(ofType(timeBasedRouteItemDialogSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         const timeBasedRouteToAdd: TimeBasedRouteItemView = {
            comment: action.timeBasedRouteItem.comment,
            endDate: action.timeBasedRouteItem.endDate,
            endTime: action.timeBasedRouteItem.endTime
               ? this.convertTimeStringToDate(action.timeBasedRouteItem.endTime)
               : null,
            isDeleted: false,
            activeDaysString: this.getActiveDaysString(action.timeBasedRouteItem),
            priorityId: (this.extensionTimeBasedRouteItems?.length ?? 0) + 1,
            routeExtension: action.timeBasedRouteItem.routeExtension,
            selectAll: action.timeBasedRouteItem.selectAll,
            startDate: action.timeBasedRouteItem.startDate,
            startTime: action.timeBasedRouteItem.startTime
               ? this.convertTimeStringToDate(action.timeBasedRouteItem.startTime)
               : null,
            timeBasedRouteId: 0,
            isActiveMonday: action.timeBasedRouteItem.isActiveMonday,
            isActiveTuesday: action.timeBasedRouteItem.isActiveTuesday,
            isActiveWednesday: action.timeBasedRouteItem.isActiveWednesday,
            isActiveThursday: action.timeBasedRouteItem.isActiveThursday,
            isActiveFriday: action.timeBasedRouteItem.isActiveFriday,
            isActiveSaturday: action.timeBasedRouteItem.isActiveSaturday,
            isActiveSunday: action.timeBasedRouteItem.isActiveSunday,
            routeExtensionNullAction: action.timeBasedRouteItem.routeExtensionNullAction,
         };
         this.extensionTimeBasedRouteItems.push(timeBasedRouteToAdd);
         this.onChange(this.extensionTimeBasedRouteItems);
         this.changeDetectorRef.markForCheck();
      });
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this Inbound Call rule?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDeleteClick(index: number): void {
      if (this.canEdit) {
         this.deleteConfirmationDialog()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((confirmed) => {
               if (confirmed) {
                  this.extensionTimeBasedRouteItems[index].isDeleted = true;
                  this.changeDetectorRef.markForCheck();
                  this.onChange(this.extensionTimeBasedRouteItems);
               }
            });
      }
   }

   drop(event: CdkDragDrop<TimeBasedRouteItemView[]>): void {
      moveItemInArray(this.extensionTimeBasedRouteItems, event.previousIndex, event.currentIndex);
      for (let i = 0; i < this.extensionTimeBasedRouteItems.length; i++) {
         this.extensionTimeBasedRouteItems[i].priorityId = i + 1;
      }

      this.extensionTimeBasedRouteItems = [...this.extensionTimeBasedRouteItems];
      this.onChange(this.extensionTimeBasedRouteItems);
      this.changeDetectorRef.markForCheck();
   }

   writeValue(extensionTimeBasedRouteItems: TimeBasedRouteItemView[]): void {
      if (extensionTimeBasedRouteItems && extensionTimeBasedRouteItems.length > 0) {
         this.extensionTimeBasedRouteItems = this.sortTimeBasedRoutes(
            extensionTimeBasedRouteItems.map((item) => {
               return {
                  ...item,
                  isDeleted: false,
                  activeDaysString: this.getActiveDaysString(item),
               };
            })
         );
      } else {
         this.extensionTimeBasedRouteItems = [];
      }
   }

   registerOnChange(onChange: (value: Array<TimeBasedRouteItemView> | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   onEditTimeBasedRouteClick(index: number): void {
      const dialogRef = this.dialogService.openFormComponent(AddTimeBasedRouteComponent, {
         closeOnNavigation: true,
         data: {
            extension: this.extension,
            timeBasedRouteFormData: this.extensionTimeBasedRouteItems[index],
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      dialogRef
         .afterClosed()
         .pipe(
            filter((timeBasedRouteItem) => {
               return timeBasedRouteItem !== undefined;
            })
         )
         .subscribe((timeBasedRouteItem) => {
            timeBasedRouteItem.startTime = timeBasedRouteItem.startTime
               ? this.convertTimeStringToDate(timeBasedRouteItem.startTime)
               : null;
            timeBasedRouteItem.endTime = timeBasedRouteItem.endTime
               ? this.convertTimeStringToDate(timeBasedRouteItem.endTime)
               : null;
            timeBasedRouteItem.isDeleted = false;
            timeBasedRouteItem.activeDaysString = this.getActiveDaysString(timeBasedRouteItem);
            this.extensionTimeBasedRouteItems = [...this.extensionTimeBasedRouteItems];
            this.extensionTimeBasedRouteItems[index] = timeBasedRouteItem;
            this.onChange(this.extensionTimeBasedRouteItems);
            this.changeDetectorRef.markForCheck();
         });
   }

   onAddTimeBasedRouteClick() {
      this.addTimeBasedRouteClick.emit();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   private sortTimeBasedRoutes(timeBasedRoutesList: TimeBasedRouteItemView[]): TimeBasedRouteItemView[] {
      return timeBasedRoutesList.sort((t1, t2) => {
         if (!t1.priorityId || !t2.priorityId) {
            return 0;
         }

         if (!t1.priorityId || t1.priorityId > t2.priorityId) {
            return 1;
         }

         if (!t2.priorityId || t1.priorityId < t2.priorityId) {
            return -1;
         }

         return 0;
      });
   }

   private convertTimeStringToDate(timeString: string): Date {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date(Date.UTC(1970, 0, 1, hours, minutes, 0));
      return date;
   }

   private getActiveDaysString(timeBasedRouteItem) {
      const days = [
         { name: 'Su', isActive: timeBasedRouteItem.isActiveSunday },
         { name: 'M', isActive: timeBasedRouteItem.isActiveMonday },
         { name: 'Tu', isActive: timeBasedRouteItem.isActiveTuesday },
         { name: 'W', isActive: timeBasedRouteItem.isActiveWednesday },
         { name: 'Th', isActive: timeBasedRouteItem.isActiveThursday },
         { name: 'F', isActive: timeBasedRouteItem.isActiveFriday },
         { name: 'Sa', isActive: timeBasedRouteItem.isActiveSaturday },
      ];

      const activeDaysString = days
         .filter((day) => day.isActive)
         .map((day) => day.name)
         .join(', ');
      return activeDaysString;
   }
}
